import bcrypt from 'bcryptjs';
import { v4 as uuidv4 } from 'uuid';
import { prisma } from '../../shared/prisma';
import { signAccessToken, signRefreshToken, verifyRefreshToken } from '../../shared/jwt';
import { blacklistToken } from '../../shared/redis';
import { AppError } from '../../shared/errors/AppError';
import { RegisterDto, LoginDto } from './auth.schema';
import { env } from '../../config/env';

export async function registerUser(dto: RegisterDto) {
  const existing = await prisma.user.findFirst({
    where: { OR: [{ email: dto.email }, { username: dto.username }] },
  });
  if (existing) throw new AppError(409, 'Email or username already taken');

  const passwordHash = await bcrypt.hash(dto.password, 12);
  const user = await prisma.user.create({
    data: {
      email: dto.email,
      username: dto.username,
      passwordHash,
      displayName: dto.displayName,
    },
  });

  // Assign default member role
  const memberRole = await prisma.role.findUnique({ where: { name: 'member' } });
  if (memberRole) {
    await prisma.userRole.create({
      data: { userId: user.id, roleId: memberRole.id },
    });
  }

  return { id: user.id, email: user.email, username: user.username, displayName: user.displayName };
}

export async function loginUser(dto: LoginDto, deviceInfo?: string, ip?: string) {
  const user = await prisma.user.findFirst({ 
    where: {
      OR: [
        { email: dto.email },
        { username: dto.email }
      ]
    },
    include: { roles: { include: { role: true } }, department: true }
  });
  if (!user || !user.isActive) throw new AppError(401, 'Invalid credentials');

  const valid = await bcrypt.compare(dto.password, user.passwordHash);
  if (!valid) throw new AppError(401, 'Invalid credentials');

  const sessionId = uuidv4();
  const accessToken = signAccessToken({ sub: user.id, email: user.email, username: user.username });
  const refreshToken = signRefreshToken({ sub: user.id, sessionId });

  const refreshHash = await bcrypt.hash(refreshToken, 10);
  const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

  await prisma.session.create({
    data: {
      id: sessionId,
      userId: user.id,
      refreshToken: refreshHash,
      deviceInfo,
      ipAddress: ip,
      expiresAt,
    },
  });

  // Update presence
  await prisma.user.update({ where: { id: user.id }, data: { presence: 'online', lastSeen: new Date() } });

  await prisma.auditLog.create({
    data: { actorId: user.id, action: 'user.login', resourceType: 'user', ipAddress: ip },
  });

  const presetRoles = user.roles.filter((ur: any) => !ur.role.name.startsWith('custom_')).map((ur: any) => ur.role.name);
  const customRoleEntry = user.roles.find((ur: any) => ur.role.name.startsWith('custom_'));
  const permissions = customRoleEntry && Array.isArray(customRoleEntry.role.permissions) ? customRoleEntry.role.permissions : [];

  return { accessToken, refreshToken, user: { id: user.id, email: user.email, username: user.username, displayName: user.displayName, avatarUrl: user.avatarUrl, roles: presetRoles, permissions, department: user.department?.name } };
}

export async function refreshTokens(rawRefreshToken: string) {
  let payload;
  try {
    payload = verifyRefreshToken(rawRefreshToken);
  } catch {
    throw new AppError(401, 'Invalid refresh token');
  }

  const session = await prisma.session.findUnique({ where: { id: payload.sessionId } });
  if (!session || session.expiresAt < new Date()) throw new AppError(401, 'Session expired');

  const valid = await bcrypt.compare(rawRefreshToken, session.refreshToken);
  if (!valid) throw new AppError(401, 'Invalid refresh token');

  const user = await prisma.user.findUnique({ where: { id: payload.sub } });
  if (!user || !user.isActive) throw new AppError(401, 'User not found');

  // Rotate tokens
  const newAccessToken = signAccessToken({ sub: user.id, email: user.email, username: user.username });
  const newRefreshToken = signRefreshToken({ sub: user.id, sessionId: session.id });
  const newHash = await bcrypt.hash(newRefreshToken, 10);

  await prisma.session.update({
    where: { id: session.id },
    data: { refreshToken: newHash, expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) },
  });

  return { accessToken: newAccessToken, refreshToken: newRefreshToken };
}

export async function logoutUser(userId: string, sessionId: string, accessToken: string) {
  // Blacklist access token for remaining TTL (~15min max)
  await blacklistToken(accessToken, 15 * 60);
  await prisma.session.deleteMany({ where: { id: sessionId, userId } });
  await prisma.user.update({ where: { id: userId }, data: { presence: 'offline', lastSeen: new Date() } });
  await prisma.auditLog.create({ data: { actorId: userId, action: 'user.logout', resourceType: 'user' } });
}
export async function getMe(userId: string) {
  const user = await prisma.user.findUnique({
    where: { id: userId },
    select: { id: true, email: true, username: true, displayName: true, avatarUrl: true, presence: true, lastSeen: true, isActive: true, createdAt: true, roles: { include: { role: true } }, department: { select: { name: true } } },
  });
  if (!user) throw new AppError(404, 'User not found');
  
  const { roles: userRolesRelation, department, ...rest } = user;
  const presetRoles = userRolesRelation.filter((ur: any) => !ur.role.name.startsWith('custom_')).map((ur: any) => ur.role.name);
  const customRoleEntry = userRolesRelation.find((ur: any) => ur.role.name.startsWith('custom_'));
  
  let permissions: string[] = [];
  if (customRoleEntry && Array.isArray(customRoleEntry.role.permissions)) {
    permissions = customRoleEntry.role.permissions as string[];
  } else {
    const PERMISSION_MAP: Record<string, string[]> = {
      super_admin: ['*'], admin: ['*'],
      team_owner: [
        'view_activity', 'view_chat_users', 'chat_send_message', 'chat_receive_message', 'chat_edit_message', 'chat_react_message', 'chat_delete_message', 'chat_pin_message', 'chat_forward_message', 'chat_reply_message', 'chat_file_sharing', 'chat_send_attention', 'chat_text_editing', 'chat_use_emoji',
        'create_teams', 'edit_teams', 'delete_teams', 'manage_team_members', 'team_send_message', 'team_receive_message', 'team_edit_message', 'team_react_message', 'team_delete_message', 'team_pin_message', 'team_forward_message', 'team_reply_message', 'team_file_sharing', 'team_text_editing', 'team_use_emoji',
        'view_users_list'
      ],
      member: [
        'view_activity', 'view_chat_users', 'chat_send_message', 'chat_receive_message', 'chat_edit_message', 'chat_react_message', 'chat_reply_message', 'chat_file_sharing', 'chat_send_attention', 'chat_text_editing', 'chat_use_emoji',
        'team_send_message', 'team_receive_message', 'team_edit_message', 'team_react_message', 'team_reply_message', 'team_file_sharing', 'team_text_editing', 'team_use_emoji',
        'view_users_list'
      ],
      guest: [
        'view_activity', 'chat_receive_message', 'chat_react_message', 'team_receive_message', 'team_react_message'
      ],
      department_restricted: [
        'view_activity', 'view_chat_users', 'chat_send_message', 'chat_receive_message', 'chat_react_message', 'chat_reply_message', 'team_send_message', 'team_receive_message', 'team_react_message', 'team_reply_message'
      ]
    };
    presetRoles.forEach((r: string) => {
      const perms = PERMISSION_MAP[r] || [];
      perms.forEach(p => { if (!permissions.includes(p)) permissions.push(p); });
    });
  }

  return { ...rest, department: department?.name, roles: presetRoles, permissions };
}
